export type SupportedLanguageLocales =
  | "de-DE"
  | "en-US"
  | "en-GB"
  | "es-ES"
  | "fr-FR"
  | "fr-CA"
  | "it-IT"
  | "ja-JP"
  | "ko-KR"
  | "nl-NL"
  | "pt-BR"
  | "sv-SE"
  | "th-TH"
  | "zh-CN"
  | "zh-TW";

/**
 * This list of supported languages maps to:
 * https://sourcegraph.prod.tableautools.com/github.com/sf-analyticscloud/monolith@3669d96080e0f4e7fb08f2baac306deaa0d8dcdd/-/blob/workgroup/src/shared/interfaces/tab-domain-interfaces/src/com/tableausoftware/domain/LocalizationInfo.java?L48
 *
 * Order of each language group matters, the first region is picked as the default for the group.
 */
const supportedLanguageLocaleCodes: Array<SupportedLanguageLocales> = [
  "de-DE",
  // en language group, "en-US" is the default of the group
  "en-US",
  "en-GB",
  "es-ES",
  // fr language group, "fr-FR" is the default of the group
  "fr-FR",
  "fr-CA",
  "it-IT",
  "ja-JP",
  "ko-KR",
  "nl-NL",
  "pt-BR",
  "sv-SE",
  "th-TH",
  // zh language group, "zh-CN" is the default of the group
  "zh-CN",
  "zh-TW",
];

const supportedLanguageLocales = supportedLanguageLocaleCodes.map(
  (langCode) => new Intl.Locale(langCode)
);

const supportedLanguagesMap = supportedLanguageLocales.reduce(
  (languageIdToLocaleMap, locale) => {
    const languageRegionString = localeToLanguageRegionString(locale);
    languageIdToLocaleMap[languageRegionString] = locale;

    // Add mapping from language to locale for the first locale of a given language
    if (languageIdToLocaleMap[locale.language] === undefined) {
      languageIdToLocaleMap[locale.language] = locale;
    }

    return languageIdToLocaleMap;
  },
  {} as { [key: string]: Intl.Locale | undefined }
);

export function tryGetServerSupportedLanguage(
  locale: Intl.Locale
): Intl.Locale | undefined {
  locale = getChineseFallbackLocale(locale);
  return supportedLanguagesMap[localeToLanguageRegionString(locale)];
}

export function tryGetSupportedLanguageWithRegionAgnosticFallback(
  locale: Intl.Locale
): Intl.Locale | undefined {
  const serverSupportedLocale = tryGetServerSupportedLanguage(locale);

  // additionally, match given locale language only, so things like en-AU will result in en-US
  if (serverSupportedLocale === undefined) {
    return supportedLanguagesMap[locale.language];
  }
  return serverSupportedLocale;
}

export const DefaultFallbackLocale = new Intl.Locale("en-US");

export function getUserPreferredBrowserLanguages(): Array<Intl.Locale> {
  return navigator.languages.map((localeCode) => new Intl.Locale(localeCode));
}

/**
 * Returns fallback Chinese locale or original locale if not Chinese
 * based on:
 * https://sourcegraph.prod.tableautools.com/github.com/sf-analyticscloud/monolith@3669d96080e0f4e7fb08f2baac306deaa0d8dcdd/-/blob/workgroup/src/shared/libraries/tab-core-i18n/src/com/tableausoftware/core/localization/service/InternationalizationService.java?L338
 * @param locale locale to test and maybe convert
 * @returns original locale or in the case of Chinese, the fallback locale
 */
function getChineseFallbackLocale(locale: Intl.Locale): Intl.Locale {
  switch (localeToLanguageRegionString(locale, "-")) {
    case "zh-HK":
    case "zh-MO":
      return new Intl.Locale("zh-TW");
    case "zh-SG":
      return new Intl.Locale("zh-CN");
    default:
      return locale;
  }
}

export function localeToLanguageRegionString(
  locale: Intl.Locale,
  separator: "_" | "-" = "_"
): string {
  return locale.region === undefined
    ? locale.language
    : `${locale.language}${separator}${locale.region}`;
}

/**
 * Similar to {@link findFirstServerSupportedLanguageOrDefault} expect does a more inclusive match
 * @param localesToMatch locales to test
 * @returns first locale that matches a supported locale or default locale
 */
export function findFirstSupportedLanguageWithRegionAgnosticFallbackOrDefault(
  localesToMatch: Intl.Locale[]
): Intl.Locale {
  return (
    localesToMatch
      .map(tryGetSupportedLanguageWithRegionAgnosticFallback)
      .find((locale) => locale !== undefined) || DefaultFallbackLocale
  );
}

/**
 * This is intended to mimic the server behavior in InternationalizationService.java getPreferredLanguage
 * https://sourcegraph.prod.tableautools.com/search?q=context%3Aglobal+repo%3A%5Egithub%5C.com%2Fsf-analyticscloud%2Fmonolith%24+file%3AInternationalizationService%5C.java+getPreferredLanguage
 * @param localesToMatch locales to test
 * @returns first locale that matches a supported locale or default locale
 */
export function findFirstServerSupportedLanguageOrDefault(
  localesToMatch: Intl.Locale[]
): Intl.Locale {
  return (
    localesToMatch
      .map(tryGetServerSupportedLanguage)
      .find((locale) => locale !== undefined) || DefaultFallbackLocale
  );
}
